import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { PurchaseOrderItem, CreatePurchaseOrderItemRequest, QuantityUpdateRequest, ItemReceiptRequest } from '../models/PurchaseOrder';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class PurchaseOrderItemService {
  private readonly baseUrl = environment.microservices.shopping;
  private readonly apiUrl = `${this.baseUrl}/purchase-order-items`;
  
  private itemsSubject = new BehaviorSubject<PurchaseOrderItem[]>([]);
  public items$ = this.itemsSubject.asObservable();

  constructor(private http: HttpClient) {}

  // ===== OPERAÇÕES BÁSICAS =====

  /**
   * Obtém todos os itens de pedidos de compra
   */
  getAllPurchaseOrderItems(): Observable<PurchaseOrderItem[]> {
    return this.http.get<PurchaseOrderItem[]>(this.apiUrl).pipe(
      tap(items => this.itemsSubject.next(items)),
      map(items => items.map(item => this.mapDates(item)))
    );
  }

  /**
   * Obtém um item por ID
   */
  getPurchaseOrderItemById(id: number): Observable<PurchaseOrderItem> {
    return this.http.get<PurchaseOrderItem>(`${this.apiUrl}/${id}`).pipe(
      map(item => this.mapDates(item))
    );
  }

  /**
   * Cria um novo item
   */
  createPurchaseOrderItem(item: CreatePurchaseOrderItemRequest): Observable<PurchaseOrderItem> {
    return this.http.post<PurchaseOrderItem>(this.apiUrl, item).pipe(
      map(item => this.mapDates(item)),
      tap(() => this.refreshItems())
    );
  }

  /**
   * Atualiza um item
   */
  updatePurchaseOrderItem(id: number, item: CreatePurchaseOrderItemRequest): Observable<any> {
    return this.http.put(`${this.apiUrl}/${id}`, item).pipe(
      tap(() => this.refreshItems())
    );
  }

  /**
   * Exclui um item
   */
  deletePurchaseOrderItem(id: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/${id}`).pipe(
      tap(() => this.refreshItems())
    );
  }

  // ===== CONSULTAS POR PEDIDO =====

  /**
   * Obtém itens por pedido de compra
   */
  getItemsByPurchaseOrder(purchaseOrderId: number): Observable<PurchaseOrderItem[]> {
    return this.http.get<PurchaseOrderItem[]>(`${this.baseUrl}/purchase-orders/${purchaseOrderId}/items`).pipe(
      map(items => items.map(item => this.mapDates(item)))
    );
  }

  /**
   * Adiciona item ao pedido de compra
   */
  addItemToPurchaseOrder(purchaseOrderId: number, item: CreatePurchaseOrderItemRequest): Observable<any> {
    return this.http.post(`${this.baseUrl}/purchase-orders/${purchaseOrderId}/items`, item).pipe(
      tap(() => this.refreshItems())
    );
  }

  /**
   * Remove item do pedido de compra
   */
  removeItemFromPurchaseOrder(purchaseOrderId: number, itemId: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}/purchase-orders/${purchaseOrderId}/items/${itemId}`).pipe(
      tap(() => this.refreshItems())
    );
  }

  /**
   * Obtém valor total por pedido de compra
   */
  getTotalValueByPurchaseOrder(purchaseOrderId: number): Observable<{ totalValue: number }> {
    return this.http.get<{ totalValue: number }>(`${this.baseUrl}/purchase-orders/${purchaseOrderId}/total-value`);
  }

  // ===== CONSULTAS AVANÇADAS =====

  /**
   * Obtém itens filtrados
   */
  getItemsFiltered(filters: any): Observable<PurchaseOrderItem[]> {
    let params = new HttpParams();
    
    Object.keys(filters).forEach(key => {
      if (filters[key] !== null && filters[key] !== undefined && filters[key] !== '') {
        params = params.set(key, filters[key].toString());
      }
    });

    return this.http.get<PurchaseOrderItem[]>(`${this.apiUrl}/filtered`, { params }).pipe(
      map(items => items.map(item => this.mapDates(item)))
    );
  }

  /**
   * Obtém itens por status
   */
  getItemsByStatus(status: string): Observable<PurchaseOrderItem[]> {
    return this.http.get<PurchaseOrderItem[]>(`${this.apiUrl}/status/${status}`).pipe(
      map(items => items.map(item => this.mapDates(item)))
    );
  }

  /**
   * Obtém item por código do produto
   */
  getItemByProductCode(productCode: string): Observable<PurchaseOrderItem> {
    return this.http.get<PurchaseOrderItem>(`${this.apiUrl}/product-code/${productCode}`).pipe(
      map(item => this.mapDates(item))
    );
  }

  // ===== CONTROLE DE QUANTIDADES =====

  /**
   * Atualiza quantidade solicitada
   */
  updateQuantityRequested(id: number, quantity: number): Observable<any> {
    const request: QuantityUpdateRequest = { quantity };
    return this.http.put(`${this.apiUrl}/${id}/quantity-requested`, request).pipe(
      tap(() => this.refreshItems())
    );
  }

  /**
   * Atualiza quantidade aprovada
   */
  updateQuantityApproved(id: number, quantity: number): Observable<any> {
    const request: QuantityUpdateRequest = { quantity };
    return this.http.put(`${this.apiUrl}/${id}/quantity-approved`, request).pipe(
      tap(() => this.refreshItems())
    );
  }

  /**
   * Atualiza quantidade recebida
   */
  updateQuantityReceived(id: number, quantity: number, receivedByStaffId: number): Observable<any> {
    const request = { quantity, receivedByStaffId };
    return this.http.put(`${this.apiUrl}/${id}/quantity-received`, request).pipe(
      tap(() => this.refreshItems())
    );
  }

  // ===== RECEBIMENTO =====

  /**
   * Marca item como recebido
   */
  markAsReceived(id: number, request: ItemReceiptRequest): Observable<any> {
    return this.http.post(`${this.apiUrl}/${id}/receive`, request).pipe(
      tap(() => this.refreshItems())
    );
  }

  /**
   * Obtém itens pendentes de recebimento
   */
  getPendingReceipt(): Observable<PurchaseOrderItem[]> {
    return this.http.get<PurchaseOrderItem[]>(`${this.apiUrl}/pending-receipt`).pipe(
      map(items => items.map(item => this.mapDates(item)))
    );
  }

  /**
   * Obtém itens pendentes de recebimento por armazém
   */
  getPendingReceiptByWarehouse(warehouseId: number): Observable<PurchaseOrderItem[]> {
    return this.http.get<PurchaseOrderItem[]>(`${this.apiUrl}/pending-receipt/warehouse/${warehouseId}`).pipe(
      map(items => items.map(item => this.mapDates(item)))
    );
  }

  // ===== CONTROLE DE QUALIDADE =====

  /**
   * Atualiza status de qualidade
   */
  updateQualityStatus(id: number, qualityStatus: string, checkedByStaffId: number, notes?: string): Observable<any> {
    const request = { qualityStatus, checkedByStaffId, notes };
    return this.http.put(`${this.apiUrl}/${id}/quality-status`, request).pipe(
      tap(() => this.refreshItems())
    );
  }

  /**
   * Obtém itens pendentes de verificação de qualidade
   */
  getPendingQualityCheck(): Observable<PurchaseOrderItem[]> {
    return this.http.get<PurchaseOrderItem[]>(`${this.apiUrl}/pending-quality-check`).pipe(
      map(items => items.map(item => this.mapDates(item)))
    );
  }

  /**
   * Obtém itens por status de qualidade
   */
  getItemsByQualityStatus(qualityStatus: string): Observable<PurchaseOrderItem[]> {
    return this.http.get<PurchaseOrderItem[]>(`${this.apiUrl}/quality-status/${qualityStatus}`).pipe(
      map(items => items.map(item => this.mapDates(item)))
    );
  }

  // ===== CONTROLE DE PREÇOS =====

  /**
   * Atualiza preço estimado
   */
  updateEstimatedPrice(id: number, price: number): Observable<any> {
    const request = { price };
    return this.http.put(`${this.apiUrl}/${id}/estimated-price`, request).pipe(
      tap(() => this.refreshItems())
    );
  }

  /**
   * Atualiza preço real
   */
  updateActualPrice(id: number, price: number): Observable<any> {
    const request = { price };
    return this.http.put(`${this.apiUrl}/${id}/actual-price`, request).pipe(
      tap(() => this.refreshItems())
    );
  }

  /**
   * Aplica desconto
   */
  applyDiscount(id: number, discountPercentage: number): Observable<any> {
    const request = { discountPercentage };
    return this.http.put(`${this.apiUrl}/${id}/discount`, request).pipe(
      tap(() => this.refreshItems())
    );
  }

  // ===== RELATÓRIOS E ESTATÍSTICAS =====

  /**
   * Obtém estatísticas de itens
   */
  getItemsStatistics(): Observable<any> {
    return this.http.get(`${this.apiUrl}/statistics`);
  }

  /**
   * Obtém itens em atraso
   */
  getOverdueItems(): Observable<PurchaseOrderItem[]> {
    return this.http.get<PurchaseOrderItem[]>(`${this.apiUrl}/overdue`).pipe(
      map(items => items.map(item => this.mapDates(item)))
    );
  }

  /**
   * Obtém itens que vencem em X dias
   */
  getItemsDueInDays(days: number): Observable<PurchaseOrderItem[]> {
    return this.http.get<PurchaseOrderItem[]>(`${this.apiUrl}/due-in/${days}`).pipe(
      map(items => items.map(item => this.mapDates(item)))
    );
  }

  // ===== OPERAÇÕES EM LOTE =====

  /**
   * Atualização de status em lote
   */
  bulkUpdateItemStatus(itemIds: number[], newStatus: string): Observable<any> {
    const request = { itemIds, newStatus };
    return this.http.post(`${this.apiUrl}/bulk/status`, request).pipe(
      tap(() => this.refreshItems())
    );
  }

  /**
   * Recebimento em lote
   */
  bulkReceiveItems(itemIds: number[], receivedByStaffId: number): Observable<any> {
    const request = { itemIds, receivedByStaffId };
    return this.http.post(`${this.apiUrl}/bulk/receive`, request).pipe(
      tap(() => this.refreshItems())
    );
  }

  /**
   * Aprovação em lote
   */
  bulkApproveItems(itemIds: number[]): Observable<any> {
    const request = { itemIds };
    return this.http.post(`${this.apiUrl}/bulk/approve`, request).pipe(
      tap(() => this.refreshItems())
    );
  }

  // ===== MÉTODOS PRIVADOS =====

  /**
   * Converte strings de data em objetos Date
   */
  private mapDates(item: any): PurchaseOrderItem {
    if (item.expectedDeliveryDate) item.expectedDeliveryDate = new Date(item.expectedDeliveryDate);
    if (item.actualDeliveryDate) item.actualDeliveryDate = new Date(item.actualDeliveryDate);
    if (item.receiptDate) item.receiptDate = new Date(item.receiptDate);
    if (item.expirationDate) item.expirationDate = new Date(item.expirationDate);
    if (item.qualityCheckDate) item.qualityCheckDate = new Date(item.qualityCheckDate);
    if (item.createdAt) item.createdAt = new Date(item.createdAt);
    if (item.updatedAt) item.updatedAt = new Date(item.updatedAt);
    
    return item as PurchaseOrderItem;
  }

  /**
   * Atualiza a lista de itens
   */
  private refreshItems(): void {
    this.getAllPurchaseOrderItems().subscribe();
  }

  // ===== MÉTODOS DE FORMATAÇÃO =====

  /**
   * Formata o status para exibição
   */
  getStatusDisplayName(status: string): string {
    const statusMap: { [key: string]: string } = {
      'Pendente': 'Pendente',
      'Aprovado': 'Aprovado',
      'Rejeitado': 'Rejeitado',
      'Recebido Parcial': 'Recebido Parcialmente',
      'Recebido Total': 'Recebido Totalmente'
    };
    return statusMap[status] || status;
  }

  /**
   * Obtém a cor do status
   */
  getStatusColor(status: string): string {
    const colorMap: { [key: string]: string } = {
      'Pendente': 'warning',
      'Aprovado': 'success',
      'Rejeitado': 'danger',
      'Recebido Parcial': 'info',
      'Recebido Total': 'success'
    };
    return colorMap[status] || 'secondary';
  }

  /**
   * Obtém o ícone do status
   */
  getStatusIcon(status: string): string {
    const iconMap: { [key: string]: string } = {
      'Pendente': 'clock',
      'Aprovado': 'check-circle',
      'Rejeitado': 'x-circle',
      'Recebido Parcial': 'package',
      'Recebido Total': 'check-square'
    };
    return iconMap[status] || 'circle';
  }

  /**
   * Formata prioridade para exibição
   */
  getPriorityDisplayName(priority?: string): string {
    if (!priority) return '';
    
    const priorityMap: { [key: string]: string } = {
      'Baixa': 'Baixa',
      'Normal': 'Normal',
      'Alta': 'Alta',
      'Urgente': 'Urgente'
    };
    return priorityMap[priority] || priority;
  }

  /**
   * Obtém a cor da prioridade
   */
  getPriorityColor(priority?: string): string {
    if (!priority) return 'secondary';
    
    const colorMap: { [key: string]: string } = {
      'Baixa': 'secondary',
      'Normal': 'info',
      'Alta': 'warning',
      'Urgente': 'danger'
    };
    return colorMap[priority] || 'secondary';
  }

  /**
   * Formata status de qualidade para exibição
   */
  getQualityStatusDisplayName(qualityStatus?: string): string {
    if (!qualityStatus) return '';
    
    const statusMap: { [key: string]: string } = {
      'Pendente': 'Pendente',
      'Aprovado': 'Aprovado',
      'Rejeitado': 'Rejeitado',
      'Em Análise': 'Em Análise'
    };
    return statusMap[qualityStatus] || qualityStatus;
  }

  /**
   * Obtém a cor do status de qualidade
   */
  getQualityStatusColor(qualityStatus?: string): string {
    if (!qualityStatus) return 'secondary';
    
    const colorMap: { [key: string]: string } = {
      'Pendente': 'warning',
      'Aprovado': 'success',
      'Rejeitado': 'danger',
      'Em Análise': 'info'
    };
    return colorMap[qualityStatus] || 'secondary';
  }

  /**
   * Calcula percentual de recebimento
   */
  calculateReceiptPercentage(item: PurchaseOrderItem): number {
    if (item.quantityApproved === 0) return 0;
    return Math.round((item.quantityReceived / item.quantityApproved) * 100);
  }

  /**
   * Verifica se item está em atraso
   */
  isItemOverdue(item: PurchaseOrderItem): boolean {
    if (!item.expectedDeliveryDate) return false;
    const today = new Date();
    return item.expectedDeliveryDate < today && !item.isFullyReceived;
  }

  /**
   * Calcula dias até a entrega
   */
  calculateDaysToDelivery(item: PurchaseOrderItem): number {
    if (!item.expectedDeliveryDate) return 0;
    const today = new Date();
    const diffTime = item.expectedDeliveryDate.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  /**
   * Verifica se item pode ser editado
   */
  canEditItem(item: PurchaseOrderItem): boolean {
    return item.status === 'Pendente' || item.status === 'Aprovado';
  }

  /**
   * Verifica se item pode ser recebido
   */
  canReceiveItem(item: PurchaseOrderItem): boolean {
    return item.status === 'Aprovado' && item.quantityReceived < item.quantityApproved;
  }

  /**
   * Verifica se item pode ser aprovado
   */
  canApproveItem(item: PurchaseOrderItem): boolean {
    return item.status === 'Pendente';
  }
}
